import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconInfoCircle, IconLock, IconDownload } from '@tabler/icons-react';

import csvTemplate from '../../assets/csv/DonorSpring Contacts Columns.csv?url';
import Button from '../buttons/Button';
import FileInfo from '../file-info/FileInfo';
import FullScreenUpload from '../imports/FullScreenUpload';
import { useImports } from '../../store/imports/hooks';
import { useTags } from '../../store/tags/hook';
import DonorGroupCards from './DonorGroupCards';
import { countFilledGroups, donorGroupTagIds, resolveDonorGroups } from './donorGroups';
import { importJobStatusToFileStatus } from '../../core/utils';
import { IMPORT_JOB_STATUS } from '../../core/constants';
import useInterval from '../../hooks/useInterval';
import StepNavBottom from './StepNavBottom';

const POLLING_INTERVAL_IN_MS = 3000;

const ImportContactsStep = ({ onPrev, onNext }) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preselectedTag, setPreselectedTag] = useState(null);
  const { imports, setImportOnboarding, getImports } = useImports();
  const { tags, contactCounts, loading: tagsLoading, getTags, getTagContactCounts } = useTags();

  useEffect(() => {
    setImportOnboarding(true);
  }, [setImportOnboarding]);

  useEffect(() => {
    getTags();
  }, [getTags]);

  const donorGroups = useMemo(() => resolveDonorGroups(tags, contactCounts), [tags, contactCounts]);
  const filledGroupCount = useMemo(() => countFilledGroups(donorGroups), [donorGroups]);

  const donorTagIds = useMemo(() => donorGroupTagIds(donorGroups), [donorGroups]);
  const donorTagIdsKey = donorTagIds.join(',');

  // Keyed on the joined ids rather than the array so this is stable across renders.
  const refreshCounts = useCallback(() => {
    if (donorTagIdsKey) getTagContactCounts(donorTagIdsKey.split(','));
  }, [donorTagIdsKey, getTagContactCounts]);

  // First load, and again whenever the set of donor tags itself changes.
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const handleImportInto = (tag) => {
    setPreselectedTag(tag);
    setUploadOpen(true);
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    setPreselectedTag(null);
    // Covers the cases the poll cannot: a cancelled import, or one that finished
    // before this component ever saw it as in-progress.
    refreshCounts();
  };

  // Includes VALIDATED, matching pages/imports.jsx. A validated job is waiting to be
  // processed, so its contacts have not landed — omitting it stopped the poll (and
  // therefore the counts) while an import was still genuinely pending.
  const isInProgress = useMemo(() => {
    return imports.some(({ status }) =>
      [
        IMPORT_JOB_STATUS.notStarted,
        IMPORT_JOB_STATUS.validating,
        IMPORT_JOB_STATUS.processing,
        IMPORT_JOB_STATUS.validated,
      ].includes(status),
    );
  }, [imports]);

  // Poll the job list and the bucket counts together so the cards track an import in
  // flight rather than sitting stale until the user navigates away and back.
  const pollWhileImporting = useCallback(() => {
    getImports();
    refreshCounts();
  }, [getImports, refreshCounts]);

  useInterval(pollWhileImporting, isInProgress ? POLLING_INTERVAL_IN_MS : null);

  /**
   * Refresh once more on the falling edge of `isInProgress`.
   *
   * Polling during the import is not sufficient on its own: contacts land at the very
   * end, so the last in-flight poll reads counts that are still mid-write. The tick
   * that observes DONE is the first moment the numbers are final, and by then the
   * interval has already been torn down.
   */
  const wasInProgressRef = useRef(false);

  useEffect(() => {
    if (wasInProgressRef.current && !isInProgress) refreshCounts();
    wasInProgressRef.current = isInProgress;
  }, [isInProgress, refreshCounts]);

  return (
    <div className="m-auto max-w-2xl space-y-12 pb-[60px] 2xl:max-w-5xl">
      <div className="space-y-4">
        <h1 className="text-h1 font-semibold">Import Contacts</h1>
      </div>

      <div className="space-y-6">
        <div className="flex space-x-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-primary-50">
            <IconInfoCircle size={20} className="text-primary-700" />
          </div>

          <div className="flex-1 space-y-6">
            <p className="text-base">
              Import your existing contacts to unlock DonorSpring&apos;s full potential. Our platform analyzes your
              audience to identify major donor prospects and uncover new fundraising opportunities, then uses these
              insights to generate tailored content recommendations for your organization.
            </p>

            <p className="text-base">
              We recommend importing your complete list of contacts to get the most value out of DonorSpring&apos;s
              analytics and recommendations.
            </p>

            <p className="text-base">
              Additionally, when you import your contacts, donations from these supporters are processed at lower
              rates—you won&apos;t be charged the 18% platform fee that applies to new donors acquired through
              DonorSpring.
            </p>
          </div>
        </div>

        <div className="flex space-x-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-green-50">
            <IconLock size={20} className="text-green-500" />
          </div>

          <div className="flex-1">
            <p className="text-base">
              Your data is secure and is never shared with any third parties or other organizations that DonorSpring
              works with.
            </p>
          </div>
        </div>

        <div>
          <Button
            title="Download CSV template"
            size="sm"
            LeftIcon={IconDownload}
            className="!h-5 border-none !p-0 text-primary-700 !shadow-none focus:ring-0"
            href={csvTemplate}
            download="DonorSpring Contacts Columns.csv"
            iconProps={{ size: 16 }}
          />
        </div>

        <DonorGroupCards
          groups={donorGroups}
          filledCount={filledGroupCount}
          onImportInto={handleImportInto}
          loading={tagsLoading && !tags.length}
        />

        <Button title="Import CSV" color="primary" onClick={() => setUploadOpen(true)} />

        <div className="space-y-2">
          {imports.map((importJob) => (
            <FileInfo
              key={`${importJob.filename}-${importJob.createdAt}`}
              name={importJob.filename}
              status={importJobStatusToFileStatus(importJob.status)}
              withAction={false}
            />
          ))}
        </div>
      </div>

      <StepNavBottom onPrev={onPrev} onNext={onNext} nextDisabled={!imports.length} />

      <FullScreenUpload open={uploadOpen} onClose={handleUploadClose} preselectedTag={preselectedTag} />
    </div>
  );
};

ImportContactsStep.propTypes = {
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default ImportContactsStep;
